import { useDocumentTitle } from '@/hooks/useDocumentTitle';

const clauses = [
  {
    title: '1. Introduction and Acceptance',
    content: `These Terms of Service ("Terms") govern your access to and use of the website located at www.stratongroup.co.za ("the Website") operated by Straton Group (PTY) Ltd ("Straton Group", "we", "us", or "our").

By accessing or using our Website, you agree to be bound by these Terms. If you do not agree to these Terms, please do not use our Website.

These Terms are governed by the laws of the Republic of South Africa, including the Electronic Communications and Transactions Act 25 of 2002 ("ECTA") and the Consumer Protection Act 68 of 2008 ("CPA") where applicable.`,
  },
  {
    title: '2. About Straton Group',
    content: `Straton Group (PTY) Ltd is a technical services company specialising in industrial automation, systems integration, and the deployment of qualified engineering specialists, headquartered in South Africa.`,
  },
  {
    title: '3. Use of the Website',
    content: `3.1. You may use our Website for lawful purposes only and in accordance with these Terms.
3.2. You agree not to use our Website in any way that violates any applicable local, national, or international law or regulation.
3.3. You agree not to transmit any unsolicited or unauthorised advertising or promotional material.
3.4. You agree not to attempt to gain unauthorised access to any part of our Website, systems, or servers.
3.5. You agree not to use our Website to transmit any harmful, offensive, or disruptive content.
3.6. We reserve the right to restrict or terminate your access to our Website at any time and for any reason, without notice.`,
  },
  {
    title: '4. Intellectual Property',
    content: `4.1. All content on our Website, including but not limited to text, graphics, logos, images, and software, is the property of Straton Group (PTY) Ltd or its content suppliers and is protected by applicable intellectual property laws.
4.2. You may view and print pages from our Website for your personal, non-commercial use only.
4.3. You may not reproduce, distribute, modify, create derivative works from, publicly display, or commercially exploit any content from our Website without our prior written consent.
4.4. The Straton Group name, logo, and all related names, logos, and trade names are trademarks of Straton Group (PTY) Ltd. You may not use these marks without our prior written consent.`,
  },
  {
    title: '5. Information on the Website',
    content: `5.1. The information on our Website is provided for general informational purposes only and does not constitute professional advice of any kind.
5.2. We make reasonable efforts to ensure the accuracy and completeness of the information on our Website, but we make no representations or warranties of any kind, express or implied, about the completeness, accuracy, reliability, suitability, or availability of the Website or the information contained therein.
5.3. The content on our Website is subject to change without notice.`,
  },
  {
    title: '6. Third-Party Links',
    content: `Our Website may contain links to third-party websites. These links are provided for your convenience only. We have no control over the content of those websites and accept no responsibility for them or for any loss or damage that may arise from your use of them. The inclusion of any link does not imply our endorsement of the linked website.`,
  },
  {
    title: '7. Limitation of Liability',
    content: `7.1. To the fullest extent permitted by applicable law, Straton Group shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of, or inability to use, our Website.
7.2. We do not exclude liability for death or personal injury caused by our negligence, fraud, or any other liability that cannot be excluded or limited by law.
7.3. Nothing in these Terms affects your statutory rights as a consumer under the Consumer Protection Act 68 of 2008 where applicable.`,
  },
  {
    title: '8. Disclaimer of Warranties',
    content: `Our Website is provided on an "as is" and "as available" basis without any warranties of any kind, either express or implied. We do not warrant that our Website will be uninterrupted, error-free, or free of viruses or other harmful components.`,
  },
  {
    title: '9. Services and Contracts',
    content: `9.1. The information on our Website regarding our services does not constitute an offer to provide services.
9.2. Any engagement of Straton Group's services will be governed by a separate written agreement between the parties.
9.3. Submission of an enquiry or contact form does not create a contractual obligation on either party.`,
  },
  {
    title: '10. Privacy',
    content: `Your use of our Website is also governed by our Privacy Policy, which is incorporated into these Terms by reference. Please review our Privacy Policy to understand our practices.`,
  },
  {
    title: '11. Electronic Communications',
    content: `When you use our Website or send emails to us, you are communicating with us electronically. You consent to receive communications from us electronically in accordance with ECTA. We will communicate with you by email or by posting notices on our Website.`,
  },
  {
    title: '12. Indemnity',
    content: `You agree to indemnify and hold harmless Straton Group (PTY) Ltd, its directors, officers, employees, and agents from and against any claims, liabilities, damages, losses, and expenses, including reasonable legal fees, arising out of or in connection with your use of our Website or your violation of these Terms.`,
  },
  {
    title: '13. Amendments',
    content: `We reserve the right to amend these Terms at any time. Amended Terms will be posted on our Website with a revised effective date. Your continued use of our Website after any amendment constitutes your acceptance of the amended Terms. We encourage you to review these Terms periodically.`,
  },
  {
    title: '14. Severability',
    content: `If any provision of these Terms is found to be invalid, unlawful, or unenforceable under any applicable law, that provision shall be deemed severed from these Terms without affecting the validity or enforceability of the remaining provisions.`,
  },
  {
    title: '15. Governing Law and Jurisdiction',
    content: `These Terms shall be governed by and construed in accordance with the laws of the Republic of South Africa. Any disputes arising from or in connection with these Terms or your use of our Website shall be subject to the exclusive jurisdiction of the South African courts.`,
  },
  {
    title: '16. Contact Us',
    content: `If you have any questions about these Terms, please contact us:

Straton Group (PTY) Ltd
Website: www.stratongroup.co.za`,
  },
];

export default function TermsOfService() {
  useDocumentTitle('Terms of Service — Straton Group');

  return (
    <main id="main-content" className="bg-bg-primary min-h-screen">
      {/* Header */}
      <section className="section-padding pt-32 pb-12">
        <div className="max-w-[1400px] mx-auto">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-8 h-px bg-text-muted" />
            <span className="text-xs uppercase tracking-[0.2em] text-text-muted">
              Legal
            </span>
          </div>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold uppercase tracking-[0.15em] text-text-primary mb-4">
            Terms of Service
          </h1>
          <p className="text-sm text-text-muted">
            Straton Group (PTY) Ltd — Effective Date: 1 June 2026
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="section-padding pb-24">
        <div className="max-w-[900px] mx-auto">
          <div className="border border-border-secondary">
            {clauses.map((clause, i) => (
              <div
                key={clause.title}
                className={`p-8 lg:p-10 ${
                  i < clauses.length - 1 ? 'border-b border-border-secondary' : ''
                }`}
              >
                <h2 className="text-sm font-semibold uppercase tracking-[0.1em] text-text-primary mb-4">
                  {clause.title}
                </h2>
                <div className="text-sm text-text-secondary leading-relaxed whitespace-pre-line">
                  {clause.content}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
